import type React from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { FlashcardRendererProps } from "./FlashcardRenderer.types";

import { CssButton, CssFlex, CssIcon } from "styles/helpers/layout";
import { FlashcardCarouselRenderer } from "./FlashcardCarouselRenderer";
import { FlashcardPracticeRenderer } from "./FlashcardPracticeRenderer";

/**
 *  This component renders the flashcards using two different methods, allowing
 * the user to switch from one to another at any time.
 *
 *  The default mode will allow users to review all the cards in the same order
 * as they come in the comment, which can be good for an initial study.
 *
 *  The practice mode will use one of the drilling components of Player to offer
 * a more interactive experience, randomizing the cards so it's more suitable for
 * the user to practice once they've reviewed the content.
 */
export const FlashcardRenderer: React.FC<FlashcardRendererProps> = ({ contentSets = [] }) => {
  const { t } = useTranslation("homepage");
  const [practiceModeEnabled, setPracticeModeEnabled] = useState(false);

  return (
    <CssFlex flexDirection="column" rowGap={2} marginTop={4}>
      <CssFlex justifyContent="flex-end">
        <CssButton
          data-cy={`flashcard-${practiceModeEnabled ? "review" : "practice"}-mode-button`}
          variant="brand"
          onClick={() => setPracticeModeEnabled(!practiceModeEnabled)}
        >
          <CssFlex flexDirection="row" alignItems="center" gap={1}>
            <CssIcon iconName={practiceModeEnabled ? "fas fa-eye" : "fas fa-random"} />
            {practiceModeEnabled
              ? t("edifyTopicDetails.flashcardRenderer.reviewCardsInOrder")
              : t("edifyTopicDetails.flashcardRenderer.enterPracticeMode")}
          </CssFlex>
        </CssButton>
      </CssFlex>
      {practiceModeEnabled ? (
        <FlashcardPracticeRenderer contentSets={contentSets} />
      ) : (
        <FlashcardCarouselRenderer contentSets={contentSets} />
      )}
    </CssFlex>
  );
};
